import React, { useEffect, useRef, useState } from 'react';
import { Pause, Play, Square } from 'lucide-react';
import {
  clockText,
  createEditQueue,
  elapsedMs,
  endEditKey,
  isRunningLong,
  pendingEditFor,
  saveWithPendingStart,
  serverNow,
  zonedHours,
} from '../lib/serverTimer.js';
import { setTabIcon, setTabTitle, resetTab } from '../lib/tabStatus.js';
import { parseTime } from '../lib/time.js';
import { TimeField } from './ui.jsx';
import { ProjectPicker } from './ProjectPicker.jsx';

const EDIT_DEBOUNCE_MS = 600;

function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// A timer edit delayed while the user is still typing (see createEditQueue).
function useDebounced(send) {
  const sendRef = useRef(send);
  sendRef.current = send;
  const queue = useRef(null);
  if (!queue.current) {
    queue.current = createEditQueue({
      send: (value) => sendRef.current(value),
      delayMs: EDIT_DEBOUNCE_MS,
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (id) => window.clearTimeout(id),
    });
  }
  useEffect(() => () => queue.current.cancel(), []);
  return queue.current;
}

// The app-bar timer. The timer itself lives on the server (useServerTimer);
// this bar shows it and turns clicks into timer actions, so every tab and
// device shows the same one and a refresh or a closed tab loses nothing.
export function TimerBar({ data, timer, limits, actions, onSaved }) {
  // Before a start, the project and description are just a local draft.
  const [draftProjectId, setDraftProjectId] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  // While active, the description is edited locally and saved debounced.
  const [description, setDescription] = useState(timer?.description || '');
  const [now, setNow] = useState(new Date());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // The save dialog's End as typed ("HH:MM"), sent with Save (null = unchanged).
  const [endAt, setEndAt] = useState(null);
  // A project picked while its PATCH is still on the way, so Save uses it.
  const pickedProject = useRef(null);
  const descriptionFocused = useRef(false);

  const status = timer?.status || 'idle';
  const elapsed = elapsedMs(timer, serverNow(timer, now));
  const runningLong = isRunningLong(timer, serverNow(timer, now), limits);

  async function run(action) {
    setError('');
    try {
      return await action();
    } catch (failure) {
      setError(failure.message || 'Timer request failed');
      return null;
    }
  }

  // Each edit is queued as { value, timerId } — the timer it was typed on, so
  // the server refuses it once that timer has been replaced.
  const descriptionEdit = useDebounced(({ value, timerId }) =>
    run(() => actions.update({ description: value }, { timerId })),
  );
  const startEdit = useDebounced(({ value, timerId }) =>
    run(() => actions.update({ startAt: value }, { timerId })),
  );

  // Adopt a description changed elsewhere (another tab, MCP) unless the user
  // is typing one right now.
  useEffect(() => {
    if (!descriptionFocused.current && !descriptionEdit.isPending())
      setDescription(timer?.description || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer?.description]);

  // A picked End belongs to this stop; drop it once the timer moves on.
  const endKey = endEditKey(timer);
  useEffect(() => setEndAt(null), [status, endKey]);

  useEffect(() => {
    if (status !== 'running') return undefined;
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, [status]);

  // Mirror the timer into the tab title so it's readable from another tab;
  // idle restores the app's own title. A forgotten timer gets a ⚠ prefix.
  useEffect(() => {
    if (status === 'idle') resetTab();
    else setTabTitle(`${runningLong ? '⚠ ' : ''}${formatElapsed(elapsed)}`);
  }, [status, elapsed, runningLong]);

  // The favicon becomes the Born Digital mark with its petals in the state
  // colour; it only needs to change when the status changes, not on every
  // one-second tick.
  useEffect(() => {
    if (status === 'idle') resetTab();
    else setTabIcon(status);
  }, [status]);

  useEffect(() => () => resetTab(), []);

  async function start() {
    setBusy(true);
    const started = await run(() =>
      actions.start({
        projectId: draftProjectId,
        description: draftDescription,
      }),
    );
    setBusy(false);
    if (started) {
      setDescription(draftDescription);
      setDraftProjectId('');
      setDraftDescription('');
    }
  }

  function changeDescription(value) {
    setDescription(value);
    descriptionEdit.schedule({ value, timerId: timer?.id });
  }

  // Leaving the field adopts a description changed elsewhere while typing,
  // unless this tab's own edit is still waiting or on its way.
  function blurDescription() {
    descriptionFocused.current = false;
    if (descriptionEdit.isIdle()) setDescription(timer?.description || '');
  }

  function changeProject(projectId) {
    pickedProject.current = projectId;
    run(() => actions.update({ projectId })).then(() => {
      if (pickedProject.current === projectId) pickedProject.current = null;
    });
  }

  // Editing the start time backdates (or forward-dates) the very first
  // segment, since that's the entry's overall start. The server reads the
  // typed time in the timer's zone, on the day the timer started.
  function setStartTime(hours) {
    const base = timer?.segments[0]?.start ?? timer?.activeStart;
    if (!base) return;
    const startAt = clockText(hours);
    if (!startAt) {
      // 24:00 isn't a start; drop what was typed on the way to it, too.
      startEdit.cancel();
      setError('Start time must be before 24:00');
      return;
    }
    startEdit.schedule({ value: startAt, timerId: timer?.id });
  }

  // In the save dialog: move the end of the last stretch back — the usual fix
  // for a timer that kept running after the work stopped.
  function setEndTime(hours) {
    const last = timer?.segments[timer.segments.length - 1];
    if (!last) return;
    setEndAt(clockText(hours, { end: true }));
  }

  async function save() {
    descriptionEdit.cancel();
    const projectId = pickedProject.current ?? timer.projectId;
    setBusy(true);
    const saved = await saveWithPendingStart({
      takePending: () => pendingEditFor(startEdit.takePending(), timer.id),
      requeue: startEdit.schedule,
      editVersion: startEdit.version,
      send: (taken) =>
        run(() =>
          actions.save({
            projectId: projectId || null,
            description,
            startAt: taken?.value,
            endAt,
          }),
        ),
    });
    setBusy(false);
    if (saved) {
      setDescription('');
      onSaved?.();
    }
  }

  function discard() {
    descriptionEdit.cancel();
    startEdit.cancel();
    run(() => actions.discard());
  }

  const lastSegment = timer?.segments[timer.segments.length - 1];
  const descriptionInput = (
    <input
      className="timer-description"
      value={description}
      onChange={(event) => changeDescription(event.target.value)}
      onFocus={() => {
        descriptionFocused.current = true;
      }}
      onBlur={blurDescription}
      placeholder="Description (optional)..."
    />
  );

  return (
    <div className="timer-shell">
      <div className="timer-bar">
        {status === 'idle' ? (
          <>
            <button className="timer-btn play" onClick={start} disabled={busy}>
              <Play size={14} /> Start timer
            </button>
            <ProjectPicker
              projects={data.projects}
              entries={data.entries}
              value={draftProjectId}
              onChange={setDraftProjectId}
              mode="picker"
            />
            <input
              className="timer-description"
              value={draftDescription}
              onChange={(event) => setDraftDescription(event.target.value)}
              placeholder="Description (optional)..."
            />
          </>
        ) : (
          <>
            {status === 'running' ? (
              <button
                className="timer-btn pause"
                onClick={() => run(actions.pause)}
              >
                <Pause size={14} /> Pause
              </button>
            ) : (
              <button
                className="timer-btn play"
                onClick={() => run(actions.resume)}
              >
                <Play size={14} /> Resume
              </button>
            )}
            <button
              className="timer-btn stop"
              onClick={() => run(() => actions.stop())}
              disabled={status === 'stopped'}
            >
              <Square size={13} /> Stop
            </button>
            <div className="timer-current">
              <ProjectPicker
                projects={data.projects}
                entries={data.entries}
                value={timer.projectId}
                onChange={changeProject}
                mode="picker"
              />
              {descriptionInput}
            </div>
            <div className="timer-start" title="Start time">
              <TimeField
                value={zonedHours(
                  timer.segments[0]?.start ?? timer.activeStart ?? now,
                  timer.timezone,
                )}
                onChange={setStartTime}
              />
            </div>
            <strong className="timer-elapsed">{formatElapsed(elapsed)}</strong>
            {runningLong && (
              <span
                className="project-status-badge status-paused"
                title={`It pauses by itself after ${limits.autoPauseHours} h. Stop it and set the real end time if you forgot it.`}
              >
                Running {limits.reminderHours} h+ — still working?
              </span>
            )}
            {status === 'paused' && timer.autoPausedAt && (
              <span
                className="project-status-badge status-paused"
                title="Nothing was saved. Resume it, or stop it and check the end time before saving."
              >
                Auto-paused after {limits?.autoPauseHours ?? ''} h
              </span>
            )}
          </>
        )}
        {error && (
          <span className="timer-error" role="alert">
            {error}
          </span>
        )}
      </div>

      {status === 'stopped' && (
        <div className="timer-popover">
          <h2>Save timed work</h2>
          <div>
            <span className="modal-label">Project</span>
            <ProjectPicker
              projects={data.projects}
              entries={data.entries}
              value={timer.projectId}
              onChange={changeProject}
              mode="picker"
            />
          </div>
          <label>
            Description
            <input
              value={description}
              onChange={(event) => changeDescription(event.target.value)}
              onFocus={() => {
                descriptionFocused.current = true;
              }}
              onBlur={blurDescription}
              placeholder="Description"
            />
          </label>
          {lastSegment && (
            <div className="timer-end">
              <span className="modal-label">End</span>
              <TimeField
                value={
                  endAt
                    ? parseTime(endAt)
                    : zonedHours(lastSegment.end, timer.timezone)
                }
                onChange={setEndTime}
              />
            </div>
          )}
          <div className="timer-segments">
            {timer.pendingEntries.map((entry, index) => (
              <span key={`${entry.workDate}-${entry.start}-${index}`}>
                {entry.workDate} · {entry.start}-{entry.end}
              </span>
            ))}
          </div>
          <div className="timer-popover-actions">
            <button className="secondary" onClick={discard} disabled={busy}>
              Discard
            </button>
            <button
              className="primary"
              onClick={save}
              disabled={
                busy || !timer.projectId || !timer.pendingEntries.length
              }
            >
              Save{' '}
              {timer.pendingEntries.length > 1
                ? `${timer.pendingEntries.length} entries`
                : 'entry'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
